// Types for the Library feature (backed by the drive microservice).
// Mirrors the Node / Folder / File / Link shapes from sales_ms_drive/swagger.yml.

export enum DriveNodeType {
  FOLDER = 1,
  FILE = 2,
  LINK = 3,
}

export enum DriveAccessLevel {
  READ = 1,
  WRITE = 2,
  ADMIN = 3,
  OWNER = 4, // auto-assigned to the root-folder creator; not granted explicitly
}

// Matches backend ContextType (note: 0-based, NOT the 1-based node access_level).
export enum DriveContextType {
  USER = 0,
  GROUP = 1,
  TAG = 2,
}

// A single direct access grant (ticket) on a node, as returned by GET /access.
export interface IDriveAccessEntry {
  context_id: string
  context_type: DriveContextType
  access_level: DriveAccessLevel
}

// A drive node — folder, file, or link; type-specific fields present only for the matching type.
// NOTE: the API serializes the discriminator as `type`, NOT `node_type` (that's only for create bodies).
export interface IDriveNode {
  retail_id_node_id: string
  type: DriveNodeType
  parent_id: string | null
  name: string
  ancestors: string[] | null
  // FOLDER: chosen icon-pack key (or a legacy image URL)
  icon?: string | null
  created_by: string
  created_at_ts: number
  updated_by?: string | null
  updated_at_ts?: number | null
  // Caller's effective access on this node (returned by GET /roots; inherited for children).
  access_level?: DriveAccessLevel
  // FILE
  s3_key?: string
  size_bytes?: number
  mime_type?: string
  // LINK
  url?: string
  // Per-user view state on files/links (present only on GET /folders/{id} contents; folders carry none).
  is_viewed?: boolean
  view_count?: number
  last_viewed_ts?: number | null
}

// Aggregate view stats for an item (file or link), from GET /files/{id}/views.
export interface IDriveViewStats {
  total_views: number
  distinct_viewers: number
}

// Per-user view record from GET /files/{id}/view-history (ADMIN); created_at_ts = first view, updated_at_ts = latest.
export interface IDriveViewHistoryEntry {
  uid: string
  view_count: number
  created_at_ts: number
  updated_at_ts: number
}

// Lean node projection from GET /nodes — the full accessible tree, cached client-side for in-memory search.
// Note the id is the bare `node_id`, NOT the composite `retail_id_node_id` used elsewhere.
export interface IDriveSearchNode {
  node_id: string
  name: string
  type: DriveNodeType
  parent_id: string | null
  updated_at_ts?: number
  icon?: string // folders only
  mime_type?: string // files only
  size_bytes?: number // files only
  url?: string // links only
  access_level?: DriveAccessLevel // caller's effective access (max over node + ancestors)
}

// Access audience granted on creation — every listed context receives READ access.
export interface IDrivePermissions {
  users: string[]
  groups: string[]
  tags: string[]
}

// Presigned S3 POST payload returned by /files/upload-url.
export interface IDriveUploadUrl {
  s3_key: string
  upload_url: string
  fields: { [key: string]: string }
}

// Per-folder contents bucket, keyed by folder node id in the store.
export interface IDriveFolderContents {
  items: IDriveNode[]
  nextCursor: string | null
  isLoading: boolean
  hasLoadedOnce: boolean
  // The folder's own name, from the top-level `name` on `GET /folders/{id}` (its children don't carry it).
  // Lets the reload case resolve the current folder's breadcrumb crumb without guessing from a child's ancestors.
  folderName?: string | null
}

// Mime types browsers render inline (served Content-Disposition: inline, opened in a new tab); the rest download.
export const isViewableInline = (mimeType?: string | null): boolean => {
  if (!mimeType) return false
  return (
    mimeType === 'application/pdf' ||
    mimeType.startsWith('image/') ||
    mimeType.startsWith('text/') ||
    mimeType.startsWith('video/') ||
    mimeType.startsWith('audio/')
  )
}

export interface IDriveState {
  roots: IDriveNode[]
  rootsLoading: boolean
  hasLoadedRoots: boolean
  contentsByFolderId: { [folderId: string]: IDriveFolderContents }
  // Cached flat index for search, fetched lazily on first real search intent.
  searchIndex: IDriveSearchNode[] | null
  searchIndexLoading: boolean
  searchIndexLoadedAt: number | null
  searchIndexTruncated: boolean
}
